import {
  ALL_COMMAND_DESCRIPTORS,
  resolveAlias,
} from "@dxf-ai-editor/command-registry";
import type { EditorCommand, EntityPreview } from "@dxf-ai-editor/contracts";

import { t } from "../i18n";
import { DRAFT_PREVIEW_BUILDERS, DRAFT_STEP_REGISTRY } from "./drafting";
// Point-filter / temporary-tracking (FROM/M2P/.X.Y.Z — ТЗ §8.6/§10.4): the
// preview-only hover override so rubber-band rendering shows the composed
// point, not the raw auxiliary pick (Codex PR #504 P2).
import { previewPointForActiveFilter } from "./pointFilterSession";

/**
 * ## Function dependency map
 *
 * Session lifecycle:
 *   normalizeDraftCommand(rawText) -> DraftCommandId | null
 *   startDraftSession(commandId, defaultProps) -> DraftSession
 *   advanceDraftSessionWithPoint(session, point) [re-exported from draftStateMachine]
 *   advanceDraftSessionWithText(session, text)   [re-exported from draftStateMachine]
 *
 * UI text builders:
 *   buildDraftPrompt(session, spaceName) -> prompt string
 *   buildAcceptedInputText(session) -> accepted-input display
 *     -> draftStepAcceptsPointInput(step)
 *   buildCommandHint(session) -> hint string
 *   buildStageStatusText(session, coord, snap) -> status bar text
 *
 * Preview entity construction:
 *   buildDraftPreviewEntity(session, props)
 *     -> defaultDraftProperties(objectProps)
 *     -> build*PreviewEntity (LINE, CIRCLE, ARC, POLYLINE, RECTANGLE,
 *        TEXT, MTEXT, BLOCK, ARRAY, HATCH, DIMENSION, MULTILEADER,
 *        REVISIONCLOUD, VIEWPORT)
 *     each build* may call distanceBetween()
 *
 * Utility:
 *   draftStepAcceptsPointInput(step) -> boolean
 *   referencePointForDraftTextInput(session) -> DraftPoint | null
 *   defaultDraftProperties(objectProps) -> default entity props
 *   distanceBetween(a, b) -> number
 */

// Core type vocabulary lives in `draftTypes.ts` so sibling state-machine
// modules can import it without re-importing the parent helpers (was
// the source of six depcruise cycles). Re-exported here for back-compat.
export type {
  DraftPoint,
  DraftCommandId,
  DraftStep,
  DraftSession,
  DraftCommitResult,
} from "./draftTypes";

import type {
  DraftPoint,
  DraftCommandId,
  DraftStep,
  DraftSession,
  DraftCommitResult,
} from "./draftTypes";


/**
 * Resolve a user-typed alias (RU/EN, case-insensitive) to a `DraftCommandId`
 * understood by `startDraftSession`. Returns `null` when no match.
 *
 * IMPLEMENTATION (2026-04-23):
 *   The flat switch over ~50 cases that previously lived here was migrated
 *   to `resolveAlias` from `@dxf-ai-editor/command-registry`. The registry
 *   is the single source of truth for alias → canonical id. Behavioural
 *   parity with the legacy switch is preserved by:
 *
 *   1. A `SPECIAL_CASE_OVERRIDES` map that intercepts inputs whose registry
 *      resolution differs from the legacy switch. Today the only entry is
 *      `INSERT` → `"BLOCK"`: the registry's INSERT descriptor maps to draft
 *      id `"INSERT"` (its own dialog flow), but the legacy switch collapsed
 *      INSERT into `"BLOCK"`. Keeping the override means existing callers
 *      that go through `normalizeDraftCommand` directly (without consulting
 *      `shellKeyboardCommandAliases` first) still observe the BLOCK-flow
 *      result. The registry's split remains correct for AI / palette code
 *      paths that consult descriptor.draftCommandId directly.
 *
 *   2. A descriptor-without-`draftCommandId` guard that returns `null`,
 *      mirroring the switch's default case for non-drafting commands such
 *      as PROPERTIES / CHPROP / MATCHPROP (which the legacy switch did not
 *      list at all).
 *
 *   The eventual AI chat integration will catch the `null` return value
 *   (after `shellKeyboardCommandAliases` and registry both miss) and route
 *   the raw text into the AI chat fallback handler.
 */
const SPECIAL_CASE_OVERRIDES: Readonly<Record<string, DraftCommandId>> = {
  // Legacy: INSERT was collapsed onto the BLOCK draft flow before the
  // dedicated INSERT dialog landed. The registry now models INSERT as a
  // first-class descriptor, but `normalizeDraftCommand` keeps the BLOCK
  // mapping for backwards compatibility; the INSERT dialog is reached
  // either via the explicit `shellKeyboardCommandAliases` table or via
  // `startDraftSession("INSERT", ...)` from dialog code paths that bypass
  // alias normalisation entirely.
  INSERT: "BLOCK",
};

export function normalizeDraftCommand(rawText: string): DraftCommandId | null {
  const normalized = rawText.trim().toUpperCase();
  if (normalized.length === 0) return null;

  // Special cases first — preserves legacy switch behaviour for inputs
  // whose registry resolution would diverge from the historical mapping.
  const override = SPECIAL_CASE_OVERRIDES[normalized];
  if (override !== undefined) return override;

  // Standard alias resolution via the command registry.
  const descriptor = resolveAlias(normalized, ALL_COMMAND_DESCRIPTORS);
  if (descriptor === null) return null;
  if (descriptor.draftCommandId === undefined) return null;
  // Cast is safe: aliases-parity tests + validate-command-registry guarantee
  // every descriptor.draftCommandId is a member of the DraftCommandId union.
  return descriptor.draftCommandId as DraftCommandId;
}

/**
 * Begin a new draft session for the given command. Optional `options`
 * lets the caller pre-arm the session with key/value metadata that the
 * state machine reads during subsequent steps. For MLEADEREDIT the
 * ribbon buttons pass `{ targetEntityId, mode: "add" | "remove" }` so
 * the state machine can skip the select + options steps when an
 * entity is already selected.
 */
export function startDraftSession(
  command: DraftCommandId,
  spaceId: string,
  options: Record<string, string> = {},
): DraftSession {
  const stepByCommand: Record<DraftCommandId, DraftStep> = {
    LINE: "line-first-point",
    POINT: "point-location",
    // BASE / БАЗА — single point pick that sets $INSBASE.
    BASE: "insbase-point",
    CIRCLE: "circle-center",
    ARC: "arc-start",
    ELLIPSE: "ellipse-center",
    PLINE: "polyline-first-point",
    RECTANG: "rectangle-first-corner",
    DONUT: "donut-inner-d",
    POLYGON: "polygon-sides-input",
    TEXT: "text-insertion-point",
    MTEXT: "mtext-first-corner",
    // BLOCK legacy default: "block-name" is a hybrid INSERT-style prompt
    // (deprecated in Phase 1 but still reachable through old shell
    // scripts). The Phase 1 Block Definition shell flow opts in via
    // `options.definitionFlow = "true"` which switches the initial step
    // to `block-select-objects`.
    BLOCK: "block-name",
    INSERT: "insert-dialog",
    // BEDIT lands on its name-prompt step unconditionally (see
    // startDraftSession's BEDIT pre-arming below for the
    // createIfMissing path). BCLOSE / BSAVE enter their "confirm or
    // no-op" state immediately; App-level code reads the active
    // BlockDefinition state to decide whether to open the prompt or
    // fire the dispatch synchronously.
    BEDIT: "bedit-prompt-name",
    BCLOSE: "bclose-confirm",
    BSAVE: "bclose-confirm",
    // ATTDEF starts by opening the Attribute Definition dialog. App.tsx
    // gates this on "active space is block-edit"; otherwise it refuses
    // the command and surfaces the "ATTDEF can only be used in Block
    // Editor" toast before the session even starts.
    ATTDEF: "attdef-dialog",
    // DDATTE/ATTEDIT/EATTEDIT land on their select step unless an
    // INSERT id is pre-armed via options.targetEntityId — in that case
    // startDraftSession advances directly to the dialog step below.
    DDATTE: "ddatte-select",
    ATTEDIT: "ddatte-select",
    EATTEDIT: "eattedit-select",
    ARRAYRECT: "arrayrect-select",
    ARRAYPOLAR: "arraypolar-select",
    ARRAYPATH: "arraypath-select",
    ARRAYEDIT: "arrayedit-select",
    HATCH: "hatch-pattern",
    HATCHEDIT: "hatchedit-select",
    DIMENSION: "dimension-first-point",
    DIMALIGNED: "dimaligned-first-point",
    DIMANGULAR: "dimangular-first-line",
    DIM3POINT: "dim3point-vertex",
    DIMRADIUS: "dimradius-select-arc",
    DIMDIAMETER: "dimdiameter-select-arc",
    DIMORDINATE: "dimordinate-feature-point",
    DIMBASELINE: "dimbaseline-second-point",
    DIMCONTINUE: "dimcontinue-second-point",
    MLEADER: "mleader-arrow-point",
    MLEADEREDIT: "mleaderedit-select",
    REVCLOUD: "revcloud-first-corner",
    WIPEOUT: "wipeout-first-corner",
    SPLINE: "spline-fit-first",
    MVIEW: "viewport-first-corner",
    // VPCLIP-INTERACTIVE — AutoCAD's real VPCLIP first prompt is
    // "Select viewport to clip:"; a pre-selected single viewport skips
    // straight to `vpclip-first-corner` (see the pre-arm override below).
    VPCLIP: "vpclip-select-viewport",
    MOVE: "move-select",
    COPY: "copy-select",
    MIRROR: "mirror-select",
    ROTATE: "rotate-select",
    SCALE: "scale-select",
    OFFSET: "offset-distance",
    STRETCH: "stretch-select-first",
    TRIM: "trim-select-edges",
    EXTEND: "extend-select-edges",
    // FIX-5 Cluster C — AutoCAD parity: FILLET starts at "Select first
    // object or [Undo/Polyline/Radius/Trim/Multiple]". The previous
    // direct-to-radius behaviour broke the live test flow because the
    // user's typed "R" keyword (which AutoCAD uses to ENTER radius
    // mode) had no recognized handler at the radius step itself.
    // Sub-flow keyword "R" / "Radius" is intercepted by
    // `isSessionKeywordInput` and routed to the text handler, which
    // transitions to the `fillet-radius` step.
    FILLET: "fillet-first-entity",
    CHAMFER: "chamfer-distances",
    BREAK: "break-select",
    JOIN: "join-select",
    EXPLODE: "explode-select",
    LENGTHEN: "lengthen-select",
    GCCONSTRAINT: "geom-constraint-pick-first",
    // Phase K.2a — Linear parameter placement starts at base point pick.
    "BPARAM-LINEAR": "param-linear-base",
    // Phase K.2b — remaining parameter placements.
    "BPARAM-POINT": "param-point-place",
    "BPARAM-BASEPOINT": "param-basepoint-place",
    "BPARAM-VISIBILITY": "param-visibility-place",
    "BPARAM-LOOKUP": "param-lookup-place",
    "BPARAM-FLIP": "param-flip-base",
    "BPARAM-ALIGNMENT": "param-alignment-base",
    "BPARAM-POLAR": "param-polar-base",
    "BPARAM-XY": "param-xy-base",
    "BPARAM-ROTATION": "param-rotation-base",
    // Phase K.3 — action placements all begin at the parameter-pick step.
    "BACTION-MOVE": "action-move-pick-parameter",
    "BACTION-SCALE": "action-scale-pick-parameter",
    "BACTION-STRETCH": "action-stretch-pick-parameter",
    "BACTION-POLAR-STRETCH": "action-polar-stretch-pick-parameter",
    "BACTION-ROTATION": "action-rotation-pick-parameter",
    "BACTION-FLIP": "action-flip-pick-parameter",
    "BACTION-ARRAY": "action-array-pick-parameter",
    "BACTION-LOOKUP": "action-lookup-pick-parameter",
  };

  // MLEADEREDIT pre-arming: if the caller passed both a `targetEntityId`
  // and a `mode` ("add" | "remove"), skip the select + options prompts
  // and land on the corresponding point-pick step directly.
  let initialStep: DraftStep = stepByCommand[command];
  if (command === "MLEADEREDIT") {
    const hasTarget = Boolean(options.targetEntityId);
    const mode = options.mode;
    if (hasTarget && mode === "add") {
      initialStep = "mleaderedit-add-leader-arrow";
    } else if (hasTarget && mode === "remove") {
      initialStep = "mleaderedit-remove-pick";
    } else if (hasTarget) {
      // Entity pre-armed but user did not pick a sub-mode — go to the
      // [Add/Remove] prompt.
      initialStep = "mleaderedit-options";
    }
  }

  // HATCHEDIT pre-arming: if a target hatch id is already pre-armed (e.g.
  // launched from a context menu on a selected HATCH), skip the select step
  // and land on the first field prompt directly (mirrors MLEADEREDIT above).
  if (command === "HATCHEDIT" && options.targetEntityId) {
    initialStep = "hatchedit-pattern";
  }

  // MOVE/COPY/MIRROR pre-arming: if targetEntityIds are provided (from
  // an active selection), skip the select step and go directly to the
  // base-point / first-point step.
  if (command === "MOVE" && options.targetEntityIds) {
    initialStep = "move-base-point";
  } else if (command === "COPY" && options.targetEntityIds) {
    initialStep = "copy-base-point";
  } else if (command === "MIRROR" && options.targetEntityIds) {
    initialStep = "mirror-first-point";
  } else if (command === "ROTATE" && options.targetEntityIds) {
    initialStep = "rotate-base-point";
  } else if (command === "SCALE" && options.targetEntityIds) {
    initialStep = "scale-base-point";
  } else if (command === "ARRAYRECT" && options.targetEntityIds) {
    initialStep = "arrayrect-base-point";
  } else if (command === "ARRAYPOLAR" && options.targetEntityIds) {
    initialStep = "arraypolar-center";
  } else if (command === "ARRAYPATH" && options.targetEntityIds) {
    initialStep = "arraypath-path";
  }

  // Attribute-editor pre-arming: when an INSERT id is pre-armed via
  // options.targetEntityId, skip the select step and go straight to
  // the dialog step. App.tsx's effect watches for ddatte-dialog /
  // eattedit-dialog and opens the corresponding dialog.
  if (command === "DDATTE" && options.targetEntityId) {
    initialStep = "ddatte-dialog";
  } else if (command === "ATTEDIT" && options.targetEntityId) {
    initialStep = "ddatte-dialog";
  } else if (command === "EATTEDIT" && options.targetEntityId) {
    initialStep = "eattedit-dialog";
  }

  // VPCLIP-INTERACTIVE pre-arming: a pre-selected single viewport (seeded
  // as `options.vpclipViewportId` by `useDraftSession.beginDrafting`) skips
  // "Select viewport to clip:" and starts at the first-corner prompt —
  // mirrors the DDATTE-family single-target pre-arm immediately above.
  if (command === "VPCLIP" && options.vpclipViewportId) {
    initialStep = "vpclip-first-corner";
  }

  // BLOCK definition flow opt-in (Phase 1): set
  // `options.definitionFlow = "true"` to override the legacy
  // INSERT-style behaviour and enter the modern Block Definition flow
  // (select-objects → base-point → dialog). When an active selection
  // exists the flow skips the select-objects step.
  if (command === "BLOCK" && options.definitionFlow === "true") {
    initialStep = options.targetEntityIds
      ? "block-base-point"
      : "block-select-objects";
  }

  return {
    command,
    step: initialStep,
    spaceId,
    points: [],
    hoverPoint: null,
    closed: false,
    options: { ...options },
  };
}

/**
 * Build the prompt-line text for the active draft session.
 *
 * **Track 2 Phase 2 (2026-05-09)** — the previous 210-case switch was
 * replaced with table-driven dispatch via `DRAFT_STEP_REGISTRY`
 * (Phase 1 foundation). Each registry entry declares:
 *  - `promptKey`        — static i18n key for the step
 *  - `promptArgs?`      — optional `{name → string}` substitutions
 *  - `resolvePromptKey?`— optional dynamic key picker for steps whose
 *                         prompt depends on session state
 *                         (e.g. `spline-fit-next`, `copy-base-point`)
 *
 * Adding a new DraftStep now requires exactly one registry entry; the
 * `Record<DraftStep, DraftStepDescriptor>` typing forces compile-time
 * exhaustiveness so future additions cannot silently fall through.
 *
 * The pure (non-React) `t()` reads from the active language store on
 * each call, so language changes propagate naturally through the React
 * render paths that invoke this function.
 */
export function buildDraftPrompt(
  session: DraftSession | null,
  activeSpaceName: string,
): string {
  if (!session) {
    return t("prompt.idle", { space: activeSpaceName });
  }
  const entry = DRAFT_STEP_REGISTRY[session.step];
  if (!entry) {
    // DraftStep is a closed union under the Record<DraftStep, ...>
    // typing of the registry, so this branch is unreachable in
    // type-safe code. Mirror the legacy default for runtime safety
    // (e.g., a corrupted session.step from an older persisted state).
    return t("prompt.idle", { command: session.command });
  }
  const key = entry.resolvePromptKey?.(session) ?? entry.promptKey;
  const args = entry.promptArgs?.(session);
  return t(key, args);
}

export function buildAcceptedInputText(session: DraftSession | null): string {
  if (!session) {
    return t("input.idle");
  }

  if (session.step.endsWith("content")) {
    return t("input.text_content");
  }

  if (session.command === "LINE" && session.step === "line-next-point") {
    return session.points.length >= 2
      ? t("input.line_continue")
      : t("input.line_next");
  }

  if (session.step === "circle-radius") {
    return t("input.circle_radius");
  }

  if (session.step === "circle-diameter") {
    return t("input.circle_diameter");
  }

  if (session.command === "PLINE") {
    return session.points.length >= 2
      ? t("input.pline_next")
      : t("input.pline_continue");
  }

  if (draftStepAcceptsPointInput(session.step)) {
    if (session.step === "block-insertion-point") {
      return t("input.block_insertion");
    }

    if (session.step === "block-scale") {
      return t("input.block_scale");
    }

    if (session.step === "block-rotation") {
      return t("input.block_rotation");
    }

    if (
      session.step === "arrayrect-base-point" ||
      session.step === "arrayrect-count"
    ) {
      return t("input.arrayrect_points");
    }

    if (session.step === "arraypolar-center") {
      return t("input.arraypolar_center");
    }

    if (session.step === "arraypath-path") {
      return t("input.arraypath_path");
    }

    if (
      session.step === "hatch-first-corner" ||
      session.step === "hatch-second-corner"
    ) {
      return t("input.hatch_corners");
    }

    if (
      session.step === "hatch-pick-internal-point" ||
      session.step === "hatch-pick-additional"
    ) {
      return t("input.hatch_internal");
    }

    if (
      session.step === "viewport-first-corner" ||
      session.step === "viewport-second-corner"
    ) {
      return session.step === "viewport-first-corner"
        ? t("input.viewport_first")
        : t("input.viewport_second");
    }

    if (session.step === "vpclip-select-viewport") {
      return t("input.vpclip_select_viewport");
    }

    if (
      session.step === "vpclip-first-corner" ||
      session.step === "vpclip-second-corner"
    ) {
      return session.step === "vpclip-first-corner"
        ? t("input.vpclip_first_corner")
        : t("input.vpclip_second_corner");
    }

    if (
      session.step === "dimradius-select-arc" ||
      session.step === "dimdiameter-select-arc"
    ) {
      return t("input.dim_arc_select");
    }

    if (
      session.step === "dimangular-first-line" ||
      session.step === "dimangular-second-line"
    ) {
      return t("input.dim_line_endpoint");
    }

    return t("input.points_generic");
  }

  if (session.step === "arrayrect-count") {
    return t("input.arrayrect_count");
  }

  if (session.step === "arrayrect-spacing") {
    return t("input.arrayrect_spacing");
  }

  if (session.step === "arraypolar-count") {
    return t("input.arraypolar_count");
  }

  if (session.step === "arraypolar-fill") {
    return t("input.arraypolar_fill");
  }

  if (session.step === "arraypath-method") {
    return t("input.arraypath_method");
  }

  if (session.step === "arraypath-params") {
    return t("input.arraypath_params");
  }

  if (session.step === "hatch-pattern") {
    return t("input.hatch_pattern");
  }

  if (session.step === "hatch-ready") {
    return t("input.hatch_ready");
  }

  if (session.step === "hatch-mode-prompt") {
    return t("input.hatch_mode");
  }

  return t("input.confirm_or_cancel");
}

export function buildCommandHint(session: DraftSession | null): string {
  if (!session) {
    return t("hint.default");
  }

  if (session.step === "line-next-point") {
    return session.points.length >= 2
      ? t("hint.line_continue")
      : t("hint.line_next");
  }

  if (session.step === "circle-radius") {
    return t("hint.circle_radius");
  }

  if (session.step === "circle-diameter") {
    return t("hint.circle_diameter");
  }

  if (session.step === "block-name") {
    return t("hint.block_name");
  }

  if (session.step === "block-scale") {
    return t("hint.block_scale");
  }

  if (session.step === "block-rotation") {
    return t("hint.block_rotation");
  }

  if (
    session.step === "arrayrect-count" ||
    session.step === "arrayrect-spacing"
  ) {
    return t("hint.arrayrect_params");
  }

  if (
    session.step === "arraypolar-count" ||
    session.step === "arraypolar-fill"
  ) {
    return t("hint.arraypolar_params");
  }

  if (
    session.step === "arraypolar-select" ||
    session.step === "arraypolar-center"
  ) {
    return t("hint.arraypolar_select_or_center");
  }

  if (
    session.step === "arraypath-select" ||
    session.step === "arraypath-path"
  ) {
    return t("hint.arraypath_select_or_path");
  }

  if (
    session.step === "arraypath-method" ||
    session.step === "arraypath-params"
  ) {
    return t("hint.arraypath_params");
  }

  if (session.step === "hatch-pattern") {
    return t("hint.hatch_pattern");
  }

  if (session.step === "viewport-first-corner") {
    return t("hint.viewport_first");
  }

  if (session.step === "vpclip-select-viewport") {
    return t("hint.vpclip_select_viewport");
  }

  if (session.command === "PLINE") {
    return t("hint.pline");
  }

  if (session.step === "block-insertion-point") {
    return t("hint.block_insertion");
  }

  if (
    session.step === "arrayrect-select" ||
    session.step === "arrayrect-base-point"
  ) {
    return t("hint.arrayrect_select_or_base");
  }

  if (
    session.step === "hatch-first-corner" ||
    session.step === "hatch-second-corner"
  ) {
    return t("hint.hatch_corners");
  }

  if (
    session.step === "hatch-mode-prompt" ||
    session.step === "hatch-pick-internal-point" ||
    session.step === "hatch-pick-additional"
  ) {
    return t("hint.hatch_internal");
  }

  if (
    session.step === "viewport-first-corner" ||
    session.step === "viewport-second-corner"
  ) {
    return t("hint.viewport_corners");
  }

  if (
    session.step === "vpclip-first-corner" ||
    session.step === "vpclip-second-corner"
  ) {
    return t("hint.vpclip_corners");
  }

  return t("hint.generic", { command: session.command });
}

export function buildStageStatusText(
  session: DraftSession | null,
  activeSpaceName: string,
  entityCount: number,
): string {
  if (session) {
    return t("stage_status.active", {
      command: session.command,
      space: activeSpaceName,
    });
  }

  if (entityCount === 0) {
    return t("stage_status.empty", {
      spaceLower: activeSpaceName.toLowerCase(),
    });
  }

  return entityCount === 1
    ? t("stage_status.count_one", { space: activeSpaceName })
    : t("stage_status.count_many", {
        count: entityCount,
        space: activeSpaceName,
      });
}

// `draftStepAcceptsPointInput` lives in `draftSharedHelpers.ts` so the
// state machine can pull it in without back-importing this module.

/**
 * Returns true when the typed text at the current draft step is a
 * known step-keyword that the active session knows how to interpret.
 *
 * Used by App.tsx's `submitCommandText` to decide whether a single-
 * character shortcut (like "L"/"S"/"C") should reach the session's
 * text handler before being interpreted as a top-level draft command
 * via `resolveShellDraftCommand`. Without this gate, AutoCAD-parity
 * keywords at multi-step prompts (MOVE select → "L" for Last,
 * CIRCLE center → "2P", COPY base-point → "D" for Displacement, etc.)
 * collide with one-letter shellKeyboardCommandAliases entries and the
 * shortcut wins, destroying the active session.
 *
 * Empty input is also treated as a session keyword at "Select objects"
 * style steps because AutoCAD's empty-Enter advances those steps.
 */
export function isSessionKeywordInput(
  step: DraftStep,
  rawText: string,
): boolean {
  const normalized = rawText.trim().toUpperCase();

  // ── Modify "Select objects" steps — empty Enter advances; "L"/"All"
  //     / Russian alias "П"/"ВСЕ" select Last/All. The full keyword
  //     resolution lives in advanceModifySessionWithText; this gate
  //     just identifies the inputs that must reach it.
  if (
    step === "move-select" ||
    step === "copy-select" ||
    step === "mirror-select" ||
    step === "rotate-select" ||
    step === "scale-select"
  ) {
    if (normalized === "") return true;
    if (
      normalized === "L" ||
      normalized === "LAST" ||
      normalized === "П" ||
      normalized === "ALL" ||
      normalized === "ВСЕ"
    ) {
      return true;
    }
  }

  // ── CIRCLE center prompt — "2P" / "3P" / "Ttr" sub-flow keywords.
  if (step === "circle-center") {
    if (
      normalized === "2P" ||
      normalized === "2POINT" ||
      normalized === "2Т" ||
      normalized === "2T" ||
      normalized === "3P" ||
      normalized === "3POINT" ||
      normalized === "3Т" ||
      normalized === "TTR" ||
      normalized === "T" ||
      normalized === "ККР"
    ) {
      return true;
    }
  }

  // ── CIRCLE radius prompt — "D"/"Diameter" toggles diameter mode.
  if (step === "circle-radius") {
    if (normalized === "D" || normalized === "DIAMETER" || normalized === "Д") {
      return true;
    }
  }

  // ── PLINE first / next point — "W"/"Width"/"Ш"/"Ширина" enters
  //     the per-segment width sub-flow (Phase C.1).
  if (
    step === "polyline-first-point" ||
    step === "polyline-next-point"
  ) {
    if (
      normalized === "W" ||
      normalized === "WIDTH" ||
      normalized === "Ш" ||
      normalized === "ШИРИНА"
    ) {
      return true;
    }
  }

  // ── ARC second-point — "C"/"Center"/"Ц"/"Центр" enters the
  //     Start-Center-End sub-flow (Phase C.3). AutoCAD prompts for
  //     this keyword on the second-point step of ARC.
  if (step === "arc-second") {
    if (
      normalized === "C" ||
      normalized === "CENTER" ||
      normalized === "Ц" ||
      normalized === "ЦЕНТР"
    ) {
      return true;
    }
  }

  // ── RECTANG first / second corner — "D"/"Dimensions"/"Р"/"Размеры"
  //     enters the dimensions sub-flow (Phase C.4).
  if (
    step === "rectangle-first-corner" ||
    step === "rectangle-second-corner"
  ) {
    if (
      normalized === "D" ||
      normalized === "DIMENSIONS" ||
      normalized === "Р" ||
      normalized === "РАЗМЕРЫ"
    ) {
      return true;
    }
  }

  // ── ROTATE angle prompt — "R"/"Reference"/"Спр"/"Ссылка" enters
  //     the reference-angle sub-flow (Phase C.5).
  if (step === "rotate-angle") {
    if (
      normalized === "R" ||
      normalized === "REFERENCE" ||
      normalized === "СПР" ||
      normalized === "ССЫЛКА"
    ) {
      return true;
    }
  }

  // ── COPY base-point prompt — "D"/"Displacement", "E"/"Exit".
  if (step === "copy-base-point") {
    if (
      normalized === "D" ||
      normalized === "DISPLACEMENT" ||
      normalized === "E" ||
      normalized === "EXIT"
    ) {
      return true;
    }
    // Empty Enter at copy-base-point is a no-op (handled by
    // advanceModifySessionWithText), but we still want to route it
    // there rather than letting it fall through to e.g. lastSubmitted
    // command repeat.
    if (normalized === "") return true;
  }

  // ── COPY destination — "E"/"Exit"/Enter ends the loop.
  if (step === "copy-destination") {
    if (
      normalized === "" ||
      normalized === "E" ||
      normalized === "EXIT" ||
      normalized === "U" ||
      normalized === "UNDO"
    ) {
      return true;
    }
  }

  // ── FIX-5 Cluster C — FILLET first-entity: AutoCAD parity sub-flow
  // keywords. "R"/"Radius"/"Р" enter the radius input step;
  // "P"/"Polyline" / "T"/"Trim" / "M"/"Multiple" / "U"/"Undo" route to
  // the matching sub-flow (only Radius is implemented now — the others
  // are future work but the gate prevents them from being misrouted to
  // the LINE/etc. shell aliases).
  if (step === "fillet-first-entity") {
    if (
      normalized === "R" ||
      normalized === "RADIUS" ||
      normalized === "Р" ||
      normalized === "P" ||
      normalized === "POLYLINE" ||
      normalized === "T" ||
      normalized === "TRIM" ||
      normalized === "M" ||
      normalized === "MULTIPLE" ||
      normalized === "U" ||
      normalized === "UNDO"
    ) {
      return true;
    }
  }

  // ── POLYGON sides input — empty Enter accepts the cached default,
  //    so it must reach the session text handler (otherwise it would
  //    fall through to the global "repeat last command" branch).
  if (step === "polygon-sides-input") {
    if (normalized === "") return true;
  }

  // ── POLYGON mode prompt — "I"/"Inscribed" / "C"/"Circumscribed" or
  //    Russian aliases. Empty Enter accepts the default (Inscribed),
  //    so the gate captures it here.
  if (step === "polygon-mode-prompt") {
    if (
      normalized === "" ||
      normalized === "I" ||
      normalized === "INSCRIBED" ||
      normalized === "C" ||
      normalized === "CIRCUMSCRIBED" ||
      normalized === "В" ||
      normalized === "ВПИСАН" ||
      normalized === "ВПИСАННЫЙ" ||
      normalized === "О" ||
      normalized === "ОПИСАН" ||
      normalized === "ОПИСАННЫЙ"
    ) {
      return true;
    }
  }

  // ── REVCLOUD (REVCLOUD-MODES) — the first prompt's option keywords
  //    [Arc length/Object/Rectangular/Polygonal/Freehand/Style/Modify]
  //    + RU mirrors, and the empty-Enter <Object> default. Without this
  //    gate "A"/"O"/"P"/"S"/… would be hijacked by the one-letter shell
  //    aliases (ARC/OFFSET/…) and destroy the session.
  if (step === "revcloud-first-corner") {
    if (
      normalized === "" ||
      normalized === "A" ||
      normalized === "ARC" ||
      normalized === "ARC LENGTH" ||
      normalized === "ARCLENGTH" ||
      normalized === "Д" ||
      normalized === "ДЛИНА" ||
      normalized === "ДЛИНА ДУГИ" ||
      normalized === "O" ||
      normalized === "OBJECT" ||
      normalized === "О" ||
      normalized === "ОБЪЕКТ" ||
      normalized === "R" ||
      normalized === "RECT" ||
      normalized === "RECTANGULAR" ||
      normalized === "ПР" ||
      normalized === "ПРЯМОУГОЛЬНОЕ" ||
      normalized === "ПРЯМОУГОЛЬНАЯ" ||
      normalized === "P" ||
      normalized === "POLY" ||
      normalized === "POLYGONAL" ||
      normalized === "ПОЛ" ||
      normalized === "ПОЛИГОНАЛЬНОЕ" ||
      normalized === "ПОЛИГОНАЛЬНАЯ" ||
      normalized === "F" ||
      normalized === "FREEHAND" ||
      normalized === "Р" ||
      normalized === "РУКИ" ||
      normalized === "ОТ РУКИ" ||
      normalized === "S" ||
      normalized === "STYLE" ||
      normalized === "С" ||
      normalized === "СТИЛЬ" ||
      normalized === "M" ||
      normalized === "MODIFY" ||
      normalized === "И" ||
      normalized === "ИЗМЕНИТЬ"
    ) {
      return true;
    }
  }

  // ── REVCLOUD sub-prompts — every typed value must reach the session
  //    handler: arc-length (scalar), style (Normal/Calligraphy), the
  //    polygonal/freehand empty-Enter close, and the Yes/No reverse
  //    prompt.
  if (step === "revcloud-arc-length" || step === "revcloud-style") {
    return true;
  }
  if (
    step === "revcloud-polygonal-next" ||
    step === "revcloud-freehand-path"
  ) {
    if (normalized === "") return true;
  }
  if (step === "revcloud-object-reverse") {
    return true;
  }

  return false;
}

// `referencePointForDraftTextInput` lives in `draftSharedHelpers.ts` so
// the state machine can pull it in without back-importing this module.

export function buildDraftPreviewEntity(
  session: DraftSession | null,
  defaultObjectProperties: Record<string, string>,
): EntityPreview | null {
  // Track 2 Phase 3: 67-case switch (~600 LoC) replaced with table-driven
  // dispatch via DRAFT_PREVIEW_BUILDERS. Steps with no entry fall through
  // to null - preserves the legacy default branch byte-for-byte.
  if (!session) {
    return null;
  }
  const fn = DRAFT_PREVIEW_BUILDERS[session.step];
  if (!fn) {
    return null;
  }
  // Point-filter / temporary-tracking (FROM/M2P/.X.Y.Z): once a filter
  // stage COMPOSES the eventual point from the hover (M2P second pick,
  // ordinate rest pick), the rubber-band must render the point that will
  // actually be created, not the raw auxiliary pick. Override the hover
  // ONLY on the session handed to the preview builder — the real `session`
  // (state transitions, continuation-token direct-distance math elsewhere)
  // keeps the RAW hoverPoint. (Codex PR #504 P2.)
  const previewComposed = previewPointForActiveFilter(session);
  const previewSession = previewComposed
    ? { ...session, hoverPoint: previewComposed }
    : session;
  return fn(previewSession, {
    previewId: `draft-preview-${session.command.toLowerCase()}`,
    defaults: defaultDraftProperties(defaultObjectProperties),
  });
}

// `defaultDraftProperties` lives in `draftSharedHelpers.ts` so the
// state machine can pull it in without back-importing this module.

// `distanceBetween` lives in `draftTypes.ts` alongside the shared type
// vocabulary (was a depcruise cycle source). Re-exported for
// back-compat — Phase 3 of Track 2 dropped the local consumer
// (was used by the legacy `buildDraftPreviewEntity` switch, now lives
// inside `draftPreviewBuilders.ts`).
export { distanceBetween } from "./draftTypes";

// `circumscribingCircleFromThree`, `generatePolygonVertices` and
// `arcSceEndPoint` live in `draftSharedHelpers.ts` so the state machine
// can pull them in without back-importing this module.

// Re-export from sub-modules for barrel access. Helpers redirected to
// `draftSharedHelpers.ts` (was a depcruise cycle source).
export {
  createPreviewEntity,
  formatNumber,
  formatPoint,
  formatPointList,
  rectangleCorners,
  resolveArcGeometry,
  defaultDraftProperties,
  circumscribingCircleFromThree,
  generatePolygonVertices,
  arcSceEndPoint,
  draftStepAcceptsPointInput,
  referencePointForDraftTextInput,
  orthoAnchorForDraftSession,
} from "./draftSharedHelpers";

// Local imports for symbols used inside this file. Phase 3 of Track 2
// dropped the `circumscribingCircleFromThree` / `generatePolygonVertices`
// consumers — they moved to `draftPreviewBuilders.ts` along with the
// preview-entity dispatch.
import {
  defaultDraftProperties,
  draftStepAcceptsPointInput,
} from "./draftSharedHelpers";

// `advanceDraftSession*` live in `draftStateMachine.ts`. We re-export
// them here for back-compat with the existing test surface and a few
// hooks. The cycle this would normally create is avoided because
// `draftStateMachine.ts` only consumes pure helpers from
// `draftSharedHelpers.ts` and types from `draftTypes.ts` — never
// values from this module.
export {
  advanceDraftSessionWithPoint,
  advanceDraftSessionWithText,
} from "./draftStateMachine";
